import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, Home, Briefcase } from 'lucide-react';
import { buttonVariants } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui-kit/card';
import { useLanguageContext } from '@/i18n/language-context';

const templates = [
  {
    titleKey: 'LANDING_TEMPLATE_NDA_TITLE',
    descKey: 'LANDING_TEMPLATE_NDA_DESC',
    icon: FileText,
    slug: 'nda',
  },
  {
    titleKey: 'LANDING_TEMPLATE_RENTAL_TITLE',
    descKey: 'LANDING_TEMPLATE_RENTAL_DESC',
    icon: Home,
    slug: 'real-estate-purchase-agreement',
  },
  {
    titleKey: 'LANDING_TEMPLATE_EMPLOYMENT_TITLE',
    descKey: 'LANDING_TEMPLATE_EMPLOYMENT_DESC',
    icon: Briefcase,
    slug: 'employment-agreement',
  },
];

export const PopularTemplates = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageContext();
  const templateAppBaseUrl = import.meta.env.VITE_TEMPLATE_APP_BASE_URL;
  const lang = currentLanguage.split('-')[0];

  return (
    <section id="templates" className="bg-muted/30 px-6 py-24 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground font-aptos sm:text-4xl">
            {t('LANDING_TEMPLATES_SECTION_TITLE')}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto font-open-sans">
            {t('LANDING_TEMPLATES_SECTION_SUBTITLE')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map(({ titleKey, descKey, icon: Icon, slug }) => (
            <Card
              key={titleKey}
              className="group flex h-full flex-col border-transparent bg-card shadow-xl transition-all duration-200 hover:border-selise-blue hover:-translate-y-1 hover:shadow-2xl"
            >
              <CardHeader className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-selise-blue text-white ring-1 ring-selise-blue/15">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="space-y-2">
                  <CardTitle className="font-aptos text-xl leading-snug">
                    {t(titleKey)}
                  </CardTitle>
                  <CardDescription className="text-base leading-relaxed text-muted-foreground line-clamp-3 font-open-sans">
                    {t(descKey)}
                  </CardDescription>
                </div>
              </CardHeader>
              <CardFooter className="mt-auto pt-0">
                <a
                  href={`${templateAppBaseUrl}/${lang}/templates/${slug}/generate`}
                  className={cn(buttonVariants(), 'w-full bg-oxford-blue hover:bg-oxford-blue/90 text-white')}
                >
                  {t('LANDING_TEMPLATE_USE_BUTTON')}
                </a>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/templates"
            className={cn(buttonVariants({ size: 'lg' }), 'border-2 border-selise-blue bg-selise-blue hover:bg-selise-blue/90 text-white font-semibold px-10 h-14 text-lg transition-colors')}
          >
            {t('LANDING_TEMPLATES_VIEW_ALL')}
          </Link>
        </div>
      </div>
    </section>
  );
};
